import React from 'react'
import axios from 'axios'
import FlatPagination from 'material-ui-flat-pagination'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Link
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Title from '../atoms/Title'
import moment from 'moment'
import SelectInputTableHead from '../atoms/SelectInputTableHead'
import DangerouslyBox from '../atoms/DangerouslyBox'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    height: `100%`
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3)
  },
  badge: {
    '& .MuiBadge-badge': {
      height: 'auto',
      padding: 3,
      borderRadius: '50%',
      backgroundColor: '#FF6363',
      color: '#fff'
    }
  },
  searchLabel: {
    maxWidth: '142px'
  },
  searchItem: {
    '@media (min-width: 1056px)': {
      flexGrow: 1,
      maxWidth: 'none',
      flexBasis: 'none'
    }
  },
  TypographyLabel: {
    paddingBottom: 10,
    borderBottom: '1px solid #A6A6A6',
    marginBottom: 10
  }
}))

const ExpandableTableRow: React.FC<{
  children: React.ReactNode
  expandComponent: React.ReactNode
  disabled: boolean
}> = ({ children, expandComponent, disabled }) => {
  const [isExpanded, setIsExpanded] = React.useState(false)
  return (
    <React.Fragment>
      {disabled ? (
        <TableRow>{children}</TableRow>
      ) : (
        <TableRow
          style={{
            cursor: 'pointer',
            borderBottom: isExpanded && '2px solid rgba(0, 0, 0, 0.2)'
          }}
          onClick={() => setIsExpanded(prev => !prev)}
        >
          {children}
        </TableRow>
      )}

      {isExpanded && expandComponent}
    </React.Fragment>
  )
}

const MessageAllHistoryDetailList: React.FC<{ subjectGroupId: number | null }> = ({
  subjectGroupId
}) => {
  const LIMIT = 10

  const classes = useStyles({})
  const [messageSubjects, setMessageSubjects] = React.useState<any[]>([])
  const [message, setMessage] = React.useState<any>({})
  const [total, setTotal] = React.useState<number>(0)
  const [page, setPage] = React.useState<number>(1)
  const [userReadFlg, setUserReadFlg] = React.useState('')
  const [threadType, setThreadType] = React.useState('')
  const [showUserMessage, setShowMessage] = React.useState<boolean>(false)

  const fetchHistoryMessages = React.useCallback(async () => {
    if (subjectGroupId) {
      const { data } = await axios.get(
        `/messages/subject-groups/${subjectGroupId}/histories?page=${page}&limit=${LIMIT}${
          userReadFlg ? `&user_read_flg=${userReadFlg}` : ''
        }${threadType ? `&thread_type=${threadType}` : ''}`,
        {}
      )
      setMessageSubjects(data.subjects)
      setMessage(data.message)
      setTotal(Number(data.total))
    }
  }, [subjectGroupId, page, userReadFlg, threadType])

  React.useEffect(() => {
    fetchHistoryMessages()
  }, [fetchHistoryMessages])

  const handlePageChange = async (page: number) => {
    try {
      setPage(page)
    } catch (error) {
      alert(error.message)
    }
  }

  const getPageInfo = React.useCallback(() => {
    const totalPage = Math.ceil(total / LIMIT)
    const startNumber = total === 0 ? 0 : (page - 1) * LIMIT + 1
    const lastNumber = page === totalPage ? total : page * LIMIT
    return { total, totalPage, startNumber, lastNumber }
  }, [page, total])

  const CurrentPageCaptionComponent = React.useCallback(() => {
    const { total, startNumber, lastNumber } = getPageInfo()
    return (
      <Typography variant="caption" align="center" display="block" color="primary">
        全 {total} 件｜ ({startNumber} - {lastNumber}件)
      </Typography>
    )
  }, [getPageInfo])

  const handleFilterUserReadFlg = async (select: string) => {
    setPage(1)
    setUserReadFlg(select)
  }

  const handleFilterThreadType = async (select: string) => {
    setPage(1)
    setThreadType(select)
  }

  if (!message) {
    return null
  }
  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Paper className={classes.paper} square elevation={2}>
          <Title>メッセージ履歴</Title>
          <Typography variant="subtitle2" paragraph className={classes.TypographyLabel}>
            送信日時
          </Typography>
          <Typography variant="body1" paragraph>
            {message.created_at && moment(message.created_at).format('YYYY-MM-DD HH:mm:ss')}
          </Typography>
          <Typography variant="subtitle2" paragraph className={classes.TypographyLabel}>
            メッセージ件名
          </Typography>
          <Typography variant="body1" paragraph>
            {message.subject}
          </Typography>
          <Typography variant="subtitle2" paragraph className={classes.TypographyLabel}>
            プッシュ通知タイトル
          </Typography>
          <Typography variant="body1" paragraph>
            {message.notification_message}
          </Typography>
          <Typography variant="subtitle2" paragraph className={classes.TypographyLabel}>
            本文
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowMessage(!showUserMessage)}
            style={{ width: 90 }}
          >
            {showUserMessage ? '閉じる' : 'すべて見る'}
          </Button>
          {showUserMessage && (
            <Typography variant="body1" paragraph style={{ whiteSpace: 'pre-wrap' }}>
              <DangerouslyBox>{message.message}</DangerouslyBox>
            </Typography>
          )}
          <Typography
            variant="subtitle2"
            paragraph
            className={classes.TypographyLabel}
            style={{ marginTop: 16 }}
          >
            メッセージ送信対象
          </Typography>
          <Box>
            <Box display="flex" justifyContent="center">
              {CurrentPageCaptionComponent}
            </Box>
            <Box display="flex" justifyContent="center">
              <FlatPagination
                limit={LIMIT}
                offset={(page - 1) * LIMIT}
                total={total}
                onClick={(ev, offset, _page) => {
                  handlePageChange(_page)
                }}
              />
            </Box>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>送信先（氏名／会員番号）</TableCell>
                <TableCell>
                  <SelectInputTableHead
                    value={userReadFlg}
                    label={'既読/未読'}
                    selectHandler={(select: string) => {
                      handleFilterUserReadFlg(select)
                    }}
                    options={[
                      { value: '', label: 'すべて表示' },
                      { value: '0', label: '未読' },
                      { value: '1', label: '既読' }
                    ]}
                  />
                </TableCell>
                <TableCell align="right">
                  <SelectInputTableHead
                    value={threadType}
                    label={'スレッド'}
                    selectHandler={(select: string) => {
                      handleFilterThreadType(select)
                    }}
                    options={[
                      { value: '', label: 'すべて表示' },
                      { value: '1', label: '会員未読あり' },
                      { value: '0', label: '会員未読なし' },
                      { value: '3', label: '事務局未読あり' },
                      { value: '2', label: '事務局未読なし' }
                    ]}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messageSubjects.map(subject => {
                const disabled = subject.threads.length < 1
                return (
                  <ExpandableTableRow
                    key={subject.id}
                    disabled={disabled}
                    expandComponent={
                      <TableCell colSpan={4}>
                        <Card style={{ marginTop: 20, marginBottom: 20 }}>
                          <CardContent>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>本文</TableCell>
                                  <TableCell>プッシュ通知タイトル</TableCell>
                                  <TableCell>送信元</TableCell>
                                  <TableCell>送信先</TableCell>
                                  <TableCell>既読/未読</TableCell>
                                  <TableCell>送信日時</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {subject.threads.map((thread, index) => {
                                  return (
                                    <TableRow key={index}>
                                      <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                                        <DangerouslyBox>{thread.message}</DangerouslyBox>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="inherit">
                                          {thread.notification_message}
                                        </Typography>
                                      </TableCell>
                                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="inherit">{thread.sender}</Typography>
                                      </TableCell>
                                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="inherit">
                                          {thread.sender_type === 'admin'
                                            ? `${subject.name_last || ''} ${
                                                subject.name_first || ''
                                              }`
                                            : subject.sender}
                                        </Typography>
                                      </TableCell>
                                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                                        {thread.sender_type === 'admin' ? (
                                          thread.user_read_flg === 1 ? (
                                            <React.Fragment>
                                              <Typography variant="inherit">既読</Typography>
                                              {thread.user_read_at && (
                                                <Typography variant="inherit">
                                                  {`（${moment(thread.user_read_at).format(
                                                    'YYYY-MM-DD HH:mm:ss'
                                                  )}）`}
                                                </Typography>
                                              )}
                                            </React.Fragment>
                                          ) : (
                                            <Typography variant="inherit" color="secondary">
                                              未読
                                            </Typography>
                                          )
                                        ) : thread.admin_read_flg === 1 ? (
                                          <React.Fragment>
                                            <Typography variant="inherit">既読</Typography>
                                            {thread.admin_read_at && (
                                              <Typography variant="inherit">
                                                {`（${moment(thread.admin_read_at).format(
                                                  'YYYY-MM-DD HH:mm:ss'
                                                )}）`}
                                              </Typography>
                                            )}
                                          </React.Fragment>
                                        ) : subject.kaiin_no ? (
                                          <Link href={`/message-history/${subject.kaiin_no}`}>
                                            <Typography variant="inherit" color="secondary">
                                              未読
                                            </Typography>
                                          </Link>
                                        ) : (
                                          <Typography variant="inherit" color="secondary">
                                            '未読'
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="inherit">
                                          {moment(thread.thread_created_at).format(
                                            'YYYY-MM-DD hh:mm:ss'
                                          )}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  )
                                })}
                              </TableBody>
                            </Table>
                          </CardContent>
                        </Card>
                      </TableCell>
                    }
                  >
                    <TableCell>
                      <Typography>
                        {subject.name_last} {subject.name_first}{' '}
                        {subject.kaiin_no ? `（${subject.kaiin_no}）` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {subject.user_read_flg === 1 ? (
                        <Typography>
                          既読
                          {subject.user_read_at
                            ? `（${moment(subject.user_read_at).format('YYYY-MM-DD HH:mm:ss')}）`
                            : ''}
                        </Typography>
                      ) : (
                        <Typography color="secondary">未読</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end">
                        <Button variant="contained" color="primary" disabled={disabled}>
                          スレッド
                        </Button>
                        <Box ml={2}>
                          <Box>会員未読：{subject.user_unread_cnt || 0}</Box>
                          <Box>事務局未読：{subject.admin_unread_cnt || 0}</Box>
                        </Box>
                      </Box>
                    </TableCell>
                  </ExpandableTableRow>
                )
              })}
            </TableBody>
          </Table>
          <Box my={4}>
            <Box display="flex" justifyContent="center">
              {CurrentPageCaptionComponent}
            </Box>
            <Box display="flex" justifyContent="center">
              <FlatPagination
                limit={LIMIT}
                offset={(page - 1) * LIMIT}
                total={total}
                onClick={(ev, offset, _page) => {
                  handlePageChange(_page)
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Grid>
    </React.Fragment>
  )
}

export default MessageAllHistoryDetailList
